import { EstimatedPricingDetailsModal } from '@client/components/shared-pricing/EstimatedPricingDetailsModal';
import {
	estimateDisplayAtom,
	offPeakCUSAtom,
	offPeakSavingAtom,
	peakCUSAtom,
	venueEstimateUsuallySpendAtom,
} from '@client/jotai/storefront/pricing';
import { useShouldUsePersonalizedQuote } from '@hooks/use-pricing';
import { usePricingSeasonal } from '@hooks/use-pricing-seasonal';
import { Link } from '@tkww/orion-web-ds-action';
import { StatusBadge } from '@tkww/orion-web-ds-feedback';
import { TextBody, TextTitle } from '@tkww/orion-web-ds-text';
import { useAtomValue } from 'jotai';
import React, { useState } from 'react';
import type { SeasonType } from '../..';
import { getSeasonRangeLabels } from '../../utils/season';
import { EditEstimateCTA } from '../EditEstimateCTA';
import { EstimateCTA } from '../EstimateCTA';
import { EstimateDisclaimer } from '../EstimateDisclaimer';
import { PersonalizedQuoteCTA } from '../PersonalizedQuoteCTA';
import { VerticalSlide } from '../VerticalSlide';
import Styles from './styles.scss';

interface PriceCardProps {
	selectedSeason: SeasonType;
	hasSeasonToggle?: boolean;
}

const TKCusDisclaimer = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	return (
		<div className={Styles.tkCusDisclaimer}>
			<TextBody size="xs" asTyped color="subtle-dark">
				Average spend is calculated with real wedding data from couples and may
				include vendor contributions and market data insights. It is not an
				exact quote.{' '}
				<Link
					as="button"
					type="button"
					styleType="primary"
					onClick={() => setIsModalOpen(true)}
				>
					More details
				</Link>
			</TextBody>
			<EstimatedPricingDetailsModal
				isModalOpen={isModalOpen}
				closeCallback={() => setIsModalOpen(false)}
			/>
		</div>
	);
};

export const PriceCard = ({
	selectedSeason,
	hasSeasonToggle = false,
}: PriceCardProps) => {
	const peakCUS = useAtomValue(peakCUSAtom);
	const offPeakCUS = useAtomValue(offPeakCUSAtom);
	const offPeakSaving = useAtomValue(offPeakSavingAtom);
	const tkCUS = useAtomValue(venueEstimateUsuallySpendAtom);
	const estimate = useAtomValue(estimateDisplayAtom);
	const shouldUsePersonalizedQuote = useShouldUsePersonalizedQuote();
	const { peakMonths, offPeakMonths } = usePricingSeasonal();

	const peakDisplay = peakCUS || offPeakCUS || tkCUS || '';
	const offPeakDisplay = offPeakCUS || peakCUS || tkCUS || '';

	const selectedSeasonCUS = selectedSeason === 'peak' ? peakCUS : offPeakCUS;
	const otherSeasonCUS = selectedSeason === 'peak' ? offPeakCUS : peakCUS;
	const displayCUS = selectedSeasonCUS ?? otherSeasonCUS ?? tkCUS;
	const isTKCUS = !selectedSeasonCUS && !otherSeasonCUS && !!tkCUS;

	const showOffPeakSaving = offPeakSaving && selectedSeason === 'off-peak';

	const cusTitle = isTKCUS ? 'Average spend' : 'Couples usually spend';

	const getDescription = (season: SeasonType) => {
		const showSaving = offPeakSaving && season === 'off-peak';
		if (showSaving) return 'Pay less when you book off-peak season.';

		// If only one of the CUS values is available, show season range in description
		if (!!peakCUS !== !!offPeakCUS) {
			const isOnlyPeakCUS = !!peakCUS;
			const months = isOnlyPeakCUS ? peakMonths : offPeakMonths;
			const seasonRange = getSeasonRangeLabels(months);
			const seasonLabel = isOnlyPeakCUS ? 'peak' : 'off-peak';
			return `Most common price for overall services at this venue in ${seasonLabel} season ${seasonRange?.formattedRange ? `(${seasonRange.formattedRange})` : ''}.`;
		}

		return 'According to the venue, this is the most common amount spent.';
	};

	const cusDescription = getDescription(selectedSeason);

	const shouldAnimateDescription =
		hasSeasonToggle && getDescription('peak') !== getDescription('off-peak');

	if (!displayCUS && !estimate) return null;

	if (estimate) {
		return (
			<>
				<div
					className={`${Styles.priceCard} ${Styles.estimateCardEnter}`}
					data-testid="price-card"
				>
					<div className={Styles.estimateContent}>
						<TextBody size="md" bold>
							The Knot estimate *
						</TextBody>
						<TextTitle size="md">{estimate.price}</TextTitle>
						<TextBody size="sm">{estimate.description}</TextBody>
						{shouldUsePersonalizedQuote ? (
							<PersonalizedQuoteCTA />
						) : (
							<EditEstimateCTA />
						)}
					</div>
				</div>
				<EstimateDisclaimer
					estimateSourceType={estimate.estimateSourceType}
					className={Styles.estimateDisclaimer}
				/>
			</>
		);
	}

	return (
		<>
			<div className={Styles.priceCard} data-testid="price-card">
				<div className={Styles.priceCardContent}>
					<TextBody size="xl" bold>
						{cusTitle}
					</TextBody>
					<div className={Styles.cus}>
						<VerticalSlide
							activeSlot={selectedSeason}
							topValue={peakDisplay}
							bottomValue={offPeakDisplay}
						>
							{(value) => <TextTitle size="md">{value}</TextTitle>}
						</VerticalSlide>
						{showOffPeakSaving && (
							<div className={Styles.statusBadge}>
								<StatusBadge
									styleType="callOutStrong"
									size="sm"
								>{`Save up to ${offPeakSaving}`}</StatusBadge>
							</div>
						)}
					</div>
				</div>
				<div className={Styles.priceCardFooter}>
					{!isTKCUS && (
						<TextBody
							size="sm"
							color="subtle-dark"
							className={
								shouldAnimateDescription ? Styles.cusDescription : undefined
							}
							{...(shouldAnimateDescription && { key: selectedSeason })}
						>
							{cusDescription}
						</TextBody>
					)}
					{shouldUsePersonalizedQuote ? (
						<PersonalizedQuoteCTA selectedSeason={selectedSeason} />
					) : (
						<EstimateCTA selectedSeason={selectedSeason} />
					)}
				</div>
			</div>
			{isTKCUS && <TKCusDisclaimer />}
		</>
	);
};
